// Copyright ©️ 2026 eVolve MEP, LLC

import { Flex, Switch } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { isNil } from 'helpers/isNotNil';
import classes from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/SwitchRow.module.css';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import { getBestKnownBendRadius } from 'modules/Shop/EvolveFabrication/BendCreator/State/bendRadius';
import { canCenterBend, solveCenterBend } from 'modules/Shop/EvolveFabrication/BendCreator/State/centerBend';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';

/** The Center Bend switch (the `centerBend` field), for the bend types it is offered for. Turning it on turns Maximize Leg Length off. */
export const CenterBendSwitch = () => {
  const { values, setFieldValue } = useBendCreatorFormContext();
  const { scheduleValidation, lastValidation } = useBendCreatorValidationContext();
  const { conduitSizes } = useConduits();

  if (!canCenterBend(values.bendType)) return null;

  const solution = solveCenterBend(values, getBestKnownBendRadius(lastValidation, values, conduitSizes));
  const isTooLong = solution.status === 'tooLong';
  const stick = isNil(values.materialStockLength) ? 'stick' : `${String(values.materialStockLength)}' stick`;

  return (
    <AgGridStyleTooltip
      label={isTooLong ? `Too long for a ${stick}` : `Equal legs, using the whole ${stick}`}
      withArrow
      openDelay={100}
    >
      <Switch
        classNames={classes}
        label={
          <Flex gap={6} align="center">
            Center Bend
            <EvolveIcon icon="Info" size="sm" inline color="dimmed" />
          </Flex>
        }
        labelPosition="left"
        checked={values.centerBend}
        disabled={isTooLong && !values.centerBend}
        onChange={(event) => {
          const isChecked = event.currentTarget.checked;
          setFieldValue('centerBend', isChecked);
          if (isChecked) setFieldValue('maximizeLegLength', false);
          scheduleValidation('centerBend');
        }}
      />
    </AgGridStyleTooltip>
  );
};
